// AppShell — layout root.
//
// ForeFlight-style: the **map is always mounted as the base layer**.
// Non-map "pages" (Airports, Plates, Flights, Aircraft, Tracks,
// Settings, More) render as overlay sheets on top of the persistent
// map. Tapping the X (or, on desktop, the backdrop next to the sheet)
// returns to /map without remounting the map. Deep links to any
// non-map route still work because the route is matched normally — the
// shell just paints it as an overlay instead of a full takeover.
//
// Two layouts share this file:
//
// MOBILE / TABLET (< lg):
//   ┌──────────────────────────────────────┐
//   │ <main> = persistent map              │
//   │   + routed overlay (full-screen)     │
//   ├──────────────────────────────────────┤
//   │ <BottomBar>                          │
//   └──────────────────────────────────────┘
//   SideRail slides in from the left as a drawer over the main area.
//
// DESKTOP (>= lg):
//   ┌────┬─────────────────────────────────┐
//   │ R  │ <main> = persistent map         │
//   │ a  │   + routed overlay              │
//   │ i  │   (~28rem panel, map peeks      │
//   │ l  │    on the right)                │
//   └────┴─────────────────────────────────┘
//
// The /auth/agol/callback route is bare (no map / chrome) so the OAuth
// bounce isn't paint-blocked by the rest of the shell mounting.

import { ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SideRail from './SideRail';
import BottomBar from './BottomBar';
import MapPage from '../routes/MapPage';
import { useUiStore } from '../stores/uiStore';
import { useIsDesktop } from './useBreakpoint';
import { Icon } from './Icon';
import { startAirborneDetector } from '../lib/airborneDetector';
import { startAutoTrack } from '../lib/autoTrack';
import { useScreenWakeLock } from '../lib/useScreenWakeLock';
import { useAppVisibility } from '../lib/useAppVisibility';
import { useAuthStore } from '../stores/authStore';

export default function AppShell({ children }: { children: ReactNode }) {
  const railOpen = useUiStore((s) => s.railOpen);
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const navigate = useNavigate();

  const isMap = location.pathname === '/map' || location.pathname === '/';
  const isAuthCallback = location.pathname.startsWith('/auth/');
  // Full-window routes that don't want the AppShell chrome. Flight
  // weather briefing is printable + opens in a new tab; the map +
  // rail + bottom bar would just be visual noise.
  const isBare = /^\/flights\/[^/]+\/briefing/.test(location.pathname);

  // Boot the airborne detector + auto-track once for the lifetime of
  // the app shell. Both are idempotent — calling start* twice no-ops.
  useEffect(() => {
    startAirborneDetector();
    startAutoTrack();
  }, []);

  // Screen Wake Lock — Wiley's request 2026-05-11: pilots don't want
  // Always keep the screen awake while the app is open.
  // This is a flight tool — the iPad must never sleep mid-flight.
  // Uses Wake Lock API on iOS 16.4+, video fallback on older iOS / PWA.
  useScreenWakeLock(true);

  // Background-tab hygiene — pauses GPS / traffic GC / realtime
  // channels when iOS backgrounds the tab; refreshes the JWT on
  // resume so writes don't 401 silently. HI-3 audit 2026-05-22.
  useAppVisibility();

  // HI-5' (2026-05-24): sessionStale banner — shows when refreshSession
  // fails on resume. The pilot can dismiss it per-session or tap
  // Re-authenticate to navigate to the sign-in flow.
  const sessionStale = useAuthStore((s) => s.sessionStale);
  const setSessionStale = useAuthStore((s) => s.setSessionStale);
  // Dismissed state: once dismissed, don't re-show until next failure.
  const [staleDismissed, setStaleDismissed] = useState(false);
  // Reset dismissed state when sessionStale turns true again (new failure).
  useEffect(() => {
    if (sessionStale) setStaleDismissed(false);
  }, [sessionStale]);

  // Some routes (Aircraft, Flights, Track Logs, Settings) are dense
  // editors that need real estate; they get a wide overlay (~64rem)
  // with the map peeking on the far right at desktop widths. The
  // lighter-weight routes (Airports, Plates, More) stay in the slim
  // 28rem panel so the map breathes.
  const wideRoutes = ['/aircraft', '/flights', '/tracks', '/settings', '/incidents', '/crew'];
  const isWide = wideRoutes.some((p) => location.pathname.startsWith(p));

  // Bare full-screen for OAuth bounce + the printable weather
  // briefing — no map, no rail, no bar.
  if (isAuthCallback || isBare) {
    return <>{children}</>;
  }

  const showStaleBanner = sessionStale && !staleDismissed;

  return (
    <div className="flex h-full w-full bg-cockpit-900 text-cockpit-100">
      <SideRail />

      <div className="relative flex-1 flex flex-col">
        <main className="relative flex-1 overflow-hidden">
          {/* Persistent map base layer. */}
          <MapPage />

          {/* Routed page slides in as an overlay sheet on top of the
              map. /map renders nothing extra (the map is already the
              base layer). */}
          {!isMap && (
            <RouteOverlay
              isDesktop={isDesktop}
              wide={isWide}
              onClose={() => navigate('/map')}
            >
              {children}
            </RouteOverlay>
          )}
        </main>
        {!isDesktop && <BottomBar />}
      </div>

      {/* Session-stale banner (HI-5'): shown when refreshSession() fails
          on resume (token expired / LTE dead at iPad unlock). Non-blocking
          — app keeps working with cached data; pilot can dismiss or
          re-authenticate. Auto-clears on next successful refresh. */}
      {showStaleBanner && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            top: 'var(--safe-top, 0px)',
            left: 0,
            right: 0,
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 0.75rem',
            background: '#78350f',
            borderBottom: '1px solid #92400e',
            color: '#fef3c7',
            fontSize: '0.82rem',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={16}
            height={16}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>

          <span style={{ flex: 1 }}>
            Sign-in expired — re-authenticate to sync writes
          </span>

          <button
            onClick={() => navigate('/auth/supabase/callback')}
            style={{
              padding: '0.2rem 0.6rem',
              borderRadius: '0.375rem',
              border: '1px solid #92400e',
              background: '#92400e',
              color: '#fef3c7',
              fontSize: '0.8rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Re-authenticate
          </button>

          <button
            onClick={() => { setStaleDismissed(true); setSessionStale(false); }}
            aria-label="Dismiss"
            style={{
              marginLeft: '0.25rem',
              padding: '0.2rem',
              background: 'transparent',
              border: 'none',
              color: '#fde68a',
              cursor: 'pointer',
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            <Icon name="x" className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Drawer backdrop only on mobile. */}
      {railOpen && !isDesktop && (
        <button
          onClick={() => useUiStore.getState().toggleRail(false)}
          className="absolute inset-0 z-30 bg-black/40"
          aria-label="Close menu"
        />
      )}
    </div>
  );
}

/** Sheet-style container that holds the routed non-map page on top of
 *  the persistent map. Mobile: fills the map area. Desktop: 28rem
 *  panel pinned to the left so the map stays visible to the right. */
function RouteOverlay({
  children,
  onClose,
  isDesktop,
  wide,
}: {
  children: ReactNode;
  onClose: () => void;
  isDesktop: boolean;
  wide: boolean;
}) {
  // Esc closes the overlay — standard sheet/modal affordance.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      {/* Backdrop on desktop only — lets the user click off the panel
          to dismiss. Mobile sheets cover the whole map area so the
          backdrop would never be visible anyway. */}
      {isDesktop && (
        <button
          onClick={onClose}
          aria-label="Close panel"
          className="absolute inset-0 z-20 bg-black/30"
        />
      )}

      <div
        className={[
          'absolute z-30 bg-cockpit-900 shadow-rail',
          isDesktop
            ? wide
              ? 'inset-y-0 left-0 right-auto w-[min(64rem,calc(100%-3rem))] border-r border-cockpit-700'
              : 'inset-y-0 left-0 right-auto w-[28rem] border-r border-cockpit-700'
            : 'inset-0',
          'animate-slide-in',
        ].join(' ')}
      >
        {/* The X close button sits in its own safe-area-aware box so it
            stays in reach below the iOS notch / Dynamic Island. */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute z-50 right-2 h-9 w-9 rounded-lg bg-cockpit-800/95 border border-cockpit-600 hover:bg-cockpit-700 active:bg-cockpit-600 backdrop-blur flex items-center justify-center"
          style={{ top: 'calc(var(--safe-top) + 0.5rem)' }}
        >
          <Icon name="x" className="h-4 w-4" />
        </button>

        {/* Inner content stage: reserves safe-area-top so any page using
            `absolute inset-0` (which ignores parent padding) still ends
            up below the status bar. */}
        <div
          className="absolute inset-x-0 bottom-0"
          style={{ top: 'var(--safe-top)' }}
        >
          {children}
        </div>
      </div>
    </>
  );
}
